import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import apiService from "./services/apiService";
import "./CarDetail.css";
import { buildCleanMapIframe, getMapUrl } from "./pages/MyCars/Inspection";

// Constants for placeholder images
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/150";
const PLACEHOLDER_MAP = "https://via.placeholder.com/770";

const CarDetails = () => {
  const { carId } = useParams();
  const { state } = useLocation(); // Get state from navigation
  const navigate = useNavigate();
  const [carData, setCarData] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [relatedBids, setRelatedBids] = useState([]);
  const [relatedHistory, setRelatedHistory] = useState([]);

  // Function to calculate time left
  const calculateTimeLeft = (endDate) => {
    if (!endDate) return "N/A";
    const now = new Date();
    const end = new Date(endDate);
    const diffMs = end - now;
    if (diffMs <= 0) return "Auction Ended";
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    return `${days}D ${hours}h:${minutes}m:${seconds}s`;
  };

  // Function to map car data
  const mapCarData = (car) => {
    if (!car) return null;
    return {
      id: car.id || carId,
      title:
        `${car.make || ""} ${car.model || ""} ${car.variant || ""}`.trim() ||
        "Unknown Car",
      registrationNumber: car.regno || "N/A",
      rcStatus: car.rcstatus === "Yes" || car.rc,
      thumbnails:
        car.car_image_urls?.map((url, idx) => ({
          id: `${carId}-thumb-${idx}`,
          url: url || PLACEHOLDER_IMAGE,
        })) || (car.image ? [{ id: `${carId}-thumb-0`, url: car.image }] : []),
      owner:
        car.owner ||
        (car.ownership
          ? `${car.ownership}${car.ownership === 1 ? "st" : "th"} Owner`
          : "N/A"),
      kilometers: car.kmsrun || car.km || 0,
      fuelType: car.fueltype || car.fuel || "N/A",
      transmission: car["Transmission-type"] || car.transmission || "N/A",
      color: car.color || "N/A",
      timeLeft: calculateTimeLeft(car.end_date || car["ta-end-date"]),
      mapIframe: car.YardMap || null,
      location: `${car.city || "Unknown"}, ${
        car.yard || car.location || "Unknown Yard"
      }`,
      bids:
        car.bids?.map((bid, idx) => ({
          id: `${carId}-bid-${idx}`,
          amount: bid || "N/A",
        })) || [],
      bidsPlaced: car.bidsPlaced || 0,
      maxBids: car.maxBids || 5,
      history:
        car.history?.map((item, idx) => ({
          id: item.id || `${carId}-history-${idx}`,
          time: item.time || "N/A",
          text: item.text || "No description",
        })) || [],
    };
  };

  // Function to fetch car data (fallback)
  const fetchCarData = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token)
        throw new Error("No authentication token found. Please log in.");

      setLoading(true);
      console.log("Fetching car data with params:", {
        orgId: "trustauto",
        dealerId: "adminuid",
        carId,
      });

      const data = await apiService.getMyCarsByYard({
        orgId: "trustauto",
        dealerId: "adminuid",
        carId,
      });

      console.log("Raw API response:", JSON.stringify(data, null, 2));
      console.log("Provided carId:", carId);

      if (!data || typeof data !== "object") {
        console.error("Invalid API response format:", data);
        throw new Error("Invalid API response format");
      }

      let car = null;

      const yardKeys = Object.keys(data).filter(
        (key) => data[key]?.cars && Array.isArray(data[key].cars)
      );
      console.log("Yard keys found:", yardKeys);

      for (const yardKey of yardKeys) {
        const cars = data[yardKey].cars;
        console.log(
          `Inspecting cars in yard ${yardKey}:`,
          JSON.stringify(cars, null, 2)
        );
        car = cars.find((c) => {
          const match = c.car_id?.trim() === carId?.trim();
          console.log(
            `Comparing car_id: ${c.car_id} with provided carId: ${carId}, Match: ${match}`
          );
          return match;
        });
        if (car) {
          console.log("Car found in yard:", yardKey, car);
          break;
        }
      }

      if (!car && Array.isArray(data.cars)) {
        console.log("Checking direct cars array:", data.cars);
        car = data.cars.find((c) => {
          const match = c.car_id?.trim() === carId?.trim();
          console.log(
            `Comparing car_id: ${c.car_id} with provided carId: ${carId}, Match: ${match}`
          );
          return match;
        });
        if (car) console.log("Car found in direct cars array:", car);
      }

      if (!car && data.car_id?.trim() === carId?.trim()) {
        car = data;
        console.log("Car found as single object:", car);
      }

      if (!car) {
        console.error("No car found with car_id:", carId);
        throw new Error(
          `Car with ID ${carId} not found. Please check if the car ID is correct.`
        );
      }

      console.log("Selected car:", car);

      const mappedCarData = mapCarData(car);
      setCarData(mappedCarData);
      setMainImage(mappedCarData.thumbnails[0]?.url || PLACEHOLDER_IMAGE);
      setRelatedBids(mappedCarData.bids);
      setRelatedHistory(mappedCarData.history);
      setLoading(false);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to fetch car data. Please try again.");
      setLoading(false);
      if (
        err.message.includes("Authentication failed") ||
        err.message.includes("No authentication token")
      ) {
        localStorage.removeItem("authToken");
        navigate("/dealer/login"); // Updated to match /dealer/login route
      }
    }
  };

  // useEffect to handle car data
  useEffect(() => {
    if (state?.car) {
      // Use car data from state if available
      console.log("Using car data from state:", state.car);
      const mappedCarData = mapCarData(state.car);
      setCarData(mappedCarData);
      setMainImage(mappedCarData.thumbnails[0]?.url || PLACEHOLDER_IMAGE);
      setRelatedBids(mappedCarData.bids);
      setRelatedHistory(mappedCarData.history);
      setLoading(false);
    } else if (carId) {
      // Fallback to fetching data if no state is provided
      console.log("No state provided, fetching car data for carId:", carId);
      fetchCarData();
    } else {
      setError("No carId or car data provided");
      setLoading(false);
    }
  }, [carId, state]);

  const handlePlaceBid = async () => {
    if (!bidAmount || isNaN(bidAmount) || parseFloat(bidAmount) <= 0) {
      setError("Please enter a valid bid amount.");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      await apiService.placeBid({
        carId,
        bidAmount: parseFloat(bidAmount),
        token,
      });
      setBidAmount("");
      await fetchCarData(); // Refetch to update bids
    } catch (err) {
      setError("Failed to place bid: " + err.message);
    }
  };

  const loadMoreHistory = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const historyData = await apiService.getCarHistory({ carId, token });
      setRelatedHistory((prevHistory) => [
        ...prevHistory,
        ...historyData.map((item, idx) => ({
          id: item.id || `${carId}-history-${prevHistory.length + idx}`,
          time: item.time || "N/A",
          text: item.text || "No description",
        })),
      ]);
    } catch (err) {
      setError("Failed to load history: " + err.message);
    }
  };

  console.log("Rendering with carData:", carData);

  if (loading) return <div className="spinner">Loading...</div>;
  if (error)
    return (
      <div className="error">
        Error: {error}
        {/* <button onClick={fetchCarData}>Retry</button> */}
      </div>
    );
  if (!carData)
    return <div className="no-data">No car data available for ID {carId}</div>;

  return (
    <div className="car-detail-header">
      <div className="car-auction-container">
        <div className="left-section">
          <div className="header-row">
            <div className="car-title">
              <h2>{carData.title}</h2>
              <span className="variant">{carData.registrationNumber}</span>
              <span className={`rc-tag ${carData.rcStatus ? "" : "invalid"}`}>
                {carData.rcStatus ? "RC ✅" : "RC ❌"}
              </span>
            </div>
          </div>
          <div className="border">
            <img
              className="main-car-image"
              src={mainImage}
              alt="Main Car"
              onError={(e) => (e.target.src = PLACEHOLDER_IMAGE)}
            />
            <div className="thumbnail-row">
              {carData.thumbnails.map((thumb) => (
                <img
                  key={thumb.id}
                  src={thumb.url}
                  alt={`Thumbnail ${thumb.id}`}
                  className={`thumbnail ${
                    mainImage === thumb.url ? "active" : ""
                  }`}
                  onClick={() => setMainImage(thumb.url)}
                  onError={(e) => (e.target.src = PLACEHOLDER_IMAGE)}
                />
              ))}
            </div>
            <div className="specs-row">
              <span>{carData.owner}</span>
              <span>
                {carData.kilometers ? `${carData.kilometers} km` : "N/A"}
              </span>
              <span>{carData.fuelType}</span>
              <span>{carData.transmission}</span>
              <span>{carData.color}</span>
            </div>
          </div>
        </div>

        <div className="middle-section">
          <div className="time-left">
            Time Left: <strong>{carData.timeLeft}</strong>
          </div>
       <div className="map-container">

  {carData.mapIframe ? (
    <div
      className="map-iframe-wrapper"
      onClick={() => window.open(getMapUrl(carData.mapIframe), "_blank")}
      style={{ cursor: "pointer" }}
    >
    
      <div
        className="map-iframe"
        style={{ pointerEvents: "none" }}
        dangerouslySetInnerHTML={{
          __html: buildCleanMapIframe(carData.mapIframe) || 
            '<div style="background:#f0f0f0; height:200px; display:flex; align-items:center; justify-content:center; color:#999; font-size:14px;">Map not available</div>',
        }}
      />
    </div>
  ) : (
    
    <div className="map-placeholder">
     
      <img
        src={PLACEHOLDER_MAP}
        alt="Map not available"
        style={{ width: "100%", height: "200px", objectFit: "cover" }}
      />
    </div>
  )}
</div>
          <div className="location-name">📍 {carData.location}</div>

          <div className="bid-info">
            {relatedBids.length > 0 ? (
              <ul className="bid-list">
                {relatedBids.map((bid) => (
                  <li key={bid.id} className="bid-item">
                    Bid: ₹{bid.amount}
                  </li>
                ))}
              </ul>
            ) : (
              <span>No bids available</span>
            )}
          </div>

          <div className="bid-input">
            <input
              type="number"
              placeholder="₹ Enter Amount"
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              min={1}
            />
          </div>

          <div className="bids-status">
            Bids Placed{" "}
            <strong>
              {carData.bidsPlaced}/{carData.maxBids}
            </strong>
          </div>
          <button
            className="place-bid-button"
            onClick={handlePlaceBid}
            disabled={
              !bidAmount ||
              isNaN(bidAmount) ||
              parseFloat(bidAmount) <= 0 ||
              carData.bidsPlaced >= carData.maxBids
            }
          >
            Place Bid
          </button>
        </div>

        <div className="right-section">
          <h3 className="history">History</h3>
          <span className="variants" onClick={loadMoreHistory}>
            Load Previous History
          </span>
          <div className="history-items">
            {relatedHistory.length > 0 ? (
              relatedHistory.map((item) => (
                <div key={item.id} className="history-entry">
                  <div className="history-time">{item.time}</div>
                  <div className="history-text">{item.text}</div>
                </div>
              ))
            ) : (
              <div>No history available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetails;
